import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CalendarClock, PhoneCall, ArrowRight, CalendarDays } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { OpFollowCell } from './OpFollowCell';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

const Row = ({ it, left, showOwner, onSaved }) => (
  <div className="flex flex-wrap items-start gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`agenda-row-${it.id}`}>
    <div className="w-24 shrink-0 text-xs font-semibold text-slate-700">{left}</div>
    <div className="flex-1 min-w-[160px]">
      <p className="text-sm font-medium text-slate-800">{it.nome_cliente}</p>
      <p className="text-[11px] text-slate-500">
        {(it.empresa || '').toUpperCase()} · cód. {it.codigo}{showOwner && it.assigned_to_name ? ` · ${it.assigned_to_name}` : ''}
      </p>
    </div>
    <OpFollowCell item={it} onSaved={onSaved} />
  </div>
);

export const AgendaHojeCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const showOwner = ['super_admin', 'gestor', 'chefe_equipa'].includes(user?.role);

  const load = useCallback(() => {
    axios.get(`${API_URL}/agenda/hoje`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const empty = !data.hoje.length && !data.amanha.length && !data.ligar.length;

  return (
    <Card className="rounded-2xl border-violet-100" data-testid="agenda-hoje-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-violet-600" /> Agenda de Hoje
          </h2>
          <Link to="/oportunidades" className="text-xs text-violet-700 hover:underline flex items-center gap-1" data-testid="agenda-open-oportunidades">
            Abrir Oportunidades <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {empty && <p className="text-sm text-slate-400" data-testid="agenda-empty">Sem agendamentos nem clientes a ligar hoje.</p>}

        {data.hoje.length > 0 && (
          <div data-testid="agenda-hoje-list">
            <p className="text-xs uppercase tracking-wider text-violet-700 mb-1 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Agendamentos presenciais ({data.hoje.length})</p>
            {data.hoje.map((it) => <Row key={it.id} it={it} left={hhmm(it.op_agendamento)} showOwner={showOwner} onSaved={load} />)}
          </div>
        )}

        {data.ligar.length > 0 && (
          <div data-testid="agenda-ligar-list">
            <p className="text-xs uppercase tracking-wider text-rose-700 mb-1 flex items-center gap-1"><PhoneCall className="w-3.5 h-3.5" /> Clientes a ligar hoje ({data.ligar_total})</p>
            {data.ligar.map((it) => <Row key={it.id} it={it} left={<span className="font-normal text-slate-500">{it.motivo}</span>} showOwner={showOwner} onSaved={load} />)}
          </div>
        )}

        {data.amanha.length > 0 && (
          <div className="bg-slate-50 rounded-xl p-3" data-testid="agenda-amanha-list">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Amanhã ({data.amanha.length})</p>
            {data.amanha.map((it) => (
              <p key={it.id} className="text-xs text-slate-600 py-0.5">
                <span className="font-semibold">{hhmm(it.op_agendamento)}</span> · {it.nome_cliente}{showOwner && it.assigned_to_name ? ` · ${it.assigned_to_name}` : ''}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
